import React, { useCallback, useState, useEffect } from 'react'
import Calendar from 'react-calendar'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { subDays, isBefore, isFuture } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { ProfileSideBar } from './ProfileSideBar'
import { TimePicker } from 'components/TimePicker'
import { ProfileTabs } from 'components/ProfileTabs'
import { Spinner } from 'components/Spinner'
import { FormCustomSelect } from 'components/Form'

import { calendarApi, callApi, eventApi, profileApi } from 'api'

import { SUBSCRIPTION_PLAN, timezoneOptions } from 'utils/constants'

import { setPro } from 'store/actions'
import { NotificationBar } from 'components/NotificationBar'

const LOCAL_DEV_AVAILABILITY = {
  monday: { checked: true, start: '08:00 AM', end: '06:00 PM' },
  tuesday: { checked: true, start: '08:00 AM', end: '06:00 PM' },
  wednesday: { checked: true, start: '08:00 AM', end: '06:00 PM' },
  thursday: { checked: true, start: '08:00 AM', end: '06:00 PM' },
  friday: { checked: true, start: '08:00 AM', end: '06:00 PM' },
  saturday: { checked: false, start: '08:00 AM', end: '06:00 PM' },
  sunday: { checked: false, start: '08:00 AM', end: '06:00 PM' },
}

const BookPage = (props) => {
  const [timePickerData, setTimePickerData] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const history = useHistory()
  const [disabledDates, setDisabledDates] = useState([])
  const [availableDates, setAvailableDates] = useState([])
  const [pro, setPro] = useState(null)
  const [isShowCalendar, setShowCalendar] = useState(false)
  const [isBookingCalendarVisible, setBookingCalendarVisible] = useState(false)
  const [events, setEvents] = useState([])
  const [userVideos, setUserVideos] = useState(null)
  const [mobileClicked] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [disabled] = useState(false)
  const [timezone, setTimezone] = useState({ name: 'America/New_York', tag: 'en-US' })
  const [, setIsCancelingEvent] = useState(false)
  const [isNotificationVisible, setIsNotificationVisible] = useState(false)
  const [hasCalledPendingCancel, setHasCalledPendingCancel] = useState(false)

  const handleTabChange = (index) => {
    setBookingCalendarVisible(index === 0)
  }

  const handlePendingCancelEvent = useCallback(async () => {
    if (hasCalledPendingCancel) return
    setHasCalledPendingCancel(true)

    const pendingCancelItem = sessionStorage.getItem('pendingCancelCall')

    if (!pendingCancelItem) return

    const { id, type } = JSON.parse(pendingCancelItem)

    if (type !== 'group') return

    setIsCancelingEvent(true)

    try {
      const response = await callApi.unsubscribeCall(props?.user?.user?.email, id)

      if (response?.result?.result) {
        sessionStorage.removeItem('pendingCancelCall')
        setIsNotificationVisible(true)
        document.body.classList.add('canceled-call')

        setTimeout(() => {
          setIsNotificationVisible(false)
          document.body.classList.remove('canceled-call')
        }, 3000)
      }
    } catch (error) {
      console.error('Failed to cancel event:', error)
    } finally {
      setIsCancelingEvent(false)
    }
  }, [hasCalledPendingCancel, props?.user?.user?.email])

  const [calendarValue, setCalendarValue] = useState(
    new Date(formatInTimeZone(new Date(), timezone.name, 'MM/dd/yyyy HH:mm XXXX'))
  )

  const handleShowCalendar = () => {
    setShowCalendar(true)
  }

  let [called, setCalled] = useState(3)

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    const handleApi = async () => {
      props.setIsLoading(true)

      const channelName = window.location.pathname.split('/')[1]
      const proInfo = await profileApi.getProInfo({ pro: channelName })
      const proEvents = await eventApi.proEventList({ pro: channelName })
      const isLocalDev = window.location.hostname === 'localhost'

      if (!proInfo?.result?.id) {
        if (isLocalDev) {
          const localProFallback = {
            id: 1,
            category: 'Business',
            channel_name: channelName || 'steven',
            timezone: 'America/New_York',
            one_on_one_service: JSON.stringify(LOCAL_DEV_AVAILABILITY),
            user: {
              name: 'Steven Azoulay',
              email: 'steven@talkpro.com',
            },
          }

          document.title = `TalkPro | Book a call with ${localProFallback.user.name}`
          setPro(localProFallback)
          props.setPro(localProFallback)
          setAvailableDates(LOCAL_DEV_AVAILABILITY)
          setEvents([])
          setDisabledDates([])
          setIsLoading(false)
          props.setIsLoading(false)
          return
        }

        return history.push('/')
      } else {
        document.title = `TalkPro | Book a call with ${proInfo?.result?.user?.name}`
        setPro(proInfo.result)
        props.setPro(proInfo.result)
        setAvailableDates(JSON.parse(proInfo.result.one_on_one_service))
      }

      if (!proEvents.error)
        setEvents(
          proEvents?.result
            .filter((e) => isFuture(new Date(e.scheduled_at)) && e.title && !e.private)
            .sort((a, b) => new Date(a?.scheduled_at) - new Date(b?.scheduled_at))
        )

      setIsLoading(false)
      props.setIsLoading(false)

      const busyDays = await calendarApi.getBusyDays(proInfo?.result?.id)

      const dates = busyDays?.result?.pro?.items?.map((e) => {
        let formatedDate
        if (e?.start?.dateTime) {
          formatedDate = {
            start: new Date(e?.start?.dateTime),
            end: new Date(e?.end?.dateTime),
          }
        }
        return formatedDate
      })
      const filteredDates = [...new Set(dates)].filter((e) => e !== undefined)

      if (dates?.length > 0) setDisabledDates(filteredDates)
    }
    handleApi()
    window.addEventListener('resize', handleResize)
  }, [called, history, props])

  useEffect(() => {
    props.setIsBookPage(true)
    return () => {
      props.setIsBookPage(false)
    }
  }, [props])

  useEffect(() => {
    if (windowWidth >= 1024 && isShowCalendar) {
      setShowCalendar(false)
    }
  }, [isShowCalendar, windowWidth])

  useEffect(() => {
    let videoUrls = []
    if (pro?.video_urls) {
      videoUrls = JSON.parse(pro?.video_urls)
    } else if (props?.user?.video_urls) {
      videoUrls = JSON.parse(props?.user?.video_urls)
    }
    setUserVideos(videoUrls)
  }, [pro, props?.user])

  if (called > 0 && pro !== undefined) {
    setCalled(0)
  }

  useEffect(() => {
    if (props?.user) {
      if (!hasCalledPendingCancel) {
        handlePendingCancelEvent()
      }
      if (props?.user?.subscription_plan === SUBSCRIPTION_PLAN.standard) {
        window.location.replace('https://www.talkpro.com')
      }
    }
  }, [handlePendingCancelEvent, hasCalledPendingCancel, props?.user])

  return (
    <section className="section profile book_page">
      {!isLoading ? (
        <>
          {isNotificationVisible && (
            <NotificationBar>
              Your meeting was canceled, please book another
            </NotificationBar>
          )}
          <div className="book__cols book__cols--lg_mod">
            <div className="book__col book__col--sidebar">
              <ProfileSideBar profile={pro} windowWidth={windowWidth} />
            </div>
            {(windowWidth >= 1024 ||
              (windowWidth < 1024 &&
                events.length === 0 &&
                !(userVideos && userVideos.length)) ||
              mobileClicked ||
              isShowCalendar) && (
              <div className="book__col book__col--main">
                <div className="profile_calendar">
                  <div className="profile_calendar__block">
                    <div className="profile_calendar__title">Pick a day</div>
                    <Calendar
                      onChange={(e) => {
                        setTimePickerData({
                          calendarValue: e,
                          pro,
                          user: props.user,
                          disabledDates: disabledDates?.filter(
                            (e) => e.start.getDate() === calendarValue.getDate()
                          ),
                        })
                        setCalendarValue(
                          new Date(
                            formatInTimeZone(e, timezone.name, 'MM/dd/yyyy HH:mm XXXX')
                          )
                        )
                      }}
                      value={calendarValue}
                      calendarType="US"
                      maxDate={disabled}
                      showNeighboringMonth={false}
                      tileDisabled={({ date, view }) =>
                        (view === 'month' &&
                          !availableDates[
                            date
                              .toLocaleDateString('en-us', {
                                weekday: 'long',
                                // timeZone: timezone.name,
                              })
                              .toLowerCase()
                          ].checked) ||
                        isBefore(date, subDays(new Date(), 1)) ||
                        disabled
                      }
                      tileClassName={({ date, view }) =>
                        view === 'month' && date.getDay() > 0 && date.getDay() < 6
                          ? 'border_mod'
                          : ''
                      }
                    />
                  </div>

                  <div className="profile_calendar__select">
                    <FormCustomSelect
                      options={timezoneOptions}
                      onChange={(e) => {
                        setTimezone(e.value)
                        setCalendarValue(
                          new Date(
                            formatInTimeZone(
                              calendarValue,
                              timezone.name,
                              'MM/dd/yyyy HH:mm XXXX'
                            )
                          )
                        )
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="book__col book__col--aside">
              {!timePickerData && !isShowCalendar && (
                <>
                  <ProfileTabs
                    user={props?.user}
                    pro={pro}
                    events={events}
                    onTabChange={handleTabChange}
                    windowWidth={windowWidth} // Pass windowWidth to ProfileTabs
                  />
                  {!isShowCalendar && (
                    <div className="book_mobile_btn">
                      <button
                        type="button"
                        className="btn_v9"
                        onClick={handleShowCalendar}
                      >
                        Book Session
                      </button>
                    </div>
                  )}
                </>
              )}
              {isBookingCalendarVisible && windowWidth < 1024 && (
                <div className="book__col book__col--main">
                  <div className="profile_calendar">
                    <div className="profile_calendar__block">
                      <div className="profile_calendar__title">Pick a day</div>
                      <Calendar
                        onChange={(e) => {
                          setTimePickerData({
                            calendarValue: e,
                            pro,
                            user: props.user,
                            disabledDates: disabledDates?.filter(
                              (e) => e.start.getDate() === calendarValue.getDate()
                            ),
                          })
                          setCalendarValue(
                            new Date(
                              formatInTimeZone(e, timezone.name, 'MM/dd/yyyy HH:mm XXXX')
                            )
                          )
                        }}
                        value={calendarValue}
                        calendarType="US"
                        maxDate={disabled}
                        showNeighboringMonth={false}
                        tileDisabled={({ date, view }) =>
                          (view === 'month' &&
                            !availableDates[
                              date
                                .toLocaleDateString('en-us', {
                                  weekday: 'long',
                                  // timeZone: timezone.name,
                                })
                                .toLowerCase()
                            ].checked) ||
                          isBefore(date, subDays(new Date(), 1)) ||
                          disabled
                        }
                        tileClassName={({ date, view }) =>
                          view === 'month' && date.getDay() > 0 && date.getDay() < 6
                            ? 'border_mod'
                            : ''
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
              {events.length === 0 && !(userVideos && userVideos.length) && (
                <div className="book__aside_content">
                  <TimePicker
                    newDate={calendarValue}
                    timezone={timezone}
                    pro={pro}
                    user={props?.user}
                    availableDates={availableDates}
                    disabledDates={disabledDates?.filter(
                      (e) => e.start.getDate() === calendarValue.getDate()
                    )}
                  />
                </div>
              )}
              {timePickerData && (
                <div className="book__aside_content book__aside_content--mobile_popup_mod">
                  <TimePicker
                    newDate={calendarValue}
                    timezone={timezone}
                    pro={pro}
                    user={props?.user}
                    availableDates={availableDates}
                    disabledDates={disabledDates?.filter(
                      (e) => e.start.getDate() === calendarValue.getDate()
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <Spinner className="form_group__button book_mod" />
      )}
    </section>
  )
}

const mapStateToProps = ({ auth: { user, error, attributes } }) => ({
  user,
  authError: error,
  attributes,
})

const willMapDispatchToProps = {
  setPro,
}

export default connect(mapStateToProps, willMapDispatchToProps)(BookPage)
